import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useTrabajadorStore } from '../store';
import EditTrabajador from './EditTrabajador';
import ImportTrabajadoresDialog from './ImportTrabajadoresDialog';

const TrabajadoresPage: React.FC = () => {
  const {
    trabajadores,
    loading,
    dialogOpen,
    selectedTrabajador,
    fetchTrabajadores,
    fetchCatalogs,
    deleteTrabajador,
    setSelectedTrabajador,
    openDialog,
    closeDialog,
  } = useTrabajadorStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchTrabajadores();
    fetchCatalogs();
  }, [fetchTrabajadores, fetchCatalogs]);

  const filteredTrabajadores = trabajadores.filter((t) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const nombre = t.personal?.personaContacto?.toLowerCase() || '';
    const doc = t.personal?.identificacion?.toLowerCase() || '';
    const area = t.area?.descripcion?.toLowerCase() || '';
    return nombre.includes(term) || doc.includes(term) || area.includes(term);
  });

  const handleEdit = (id: number) => {
    const trabajador = trabajadores.find((t) => t.idTrabajador === id);
    if (trabajador) {
      setSelectedTrabajador(trabajador);
      openDialog();
    }
  };

  const handleCreate = () => {
    setSelectedTrabajador(null);
    openDialog();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de eliminar este trabajador?')) {
      await deleteTrabajador(id);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'identificacion',
      headerName: 'Documento',
      width: 130,
      valueGetter: (_value, row) => row.personal?.identificacion || '',
    },
    {
      field: 'nombreCompleto',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 150,
      valueGetter: (_value, row) => row.personal?.personaContacto || '',
    },
    ...(!isMobile ? [
      {
        field: 'area',
        headerName: 'Area',
        width: 150,
        valueGetter: (_value: unknown, row: any) => row.area?.descripcion || '',
      },
      {
        field: 'regimenLaboral',
        headerName: 'Regimen Laboral',
        width: 200,
        valueGetter: (_value: unknown, row: any) => row.regimenLaboral?.descripcion || '',
      },
      {
        field: 'situacion',
        headerName: 'Situacion',
        width: 160,
        valueGetter: (_value: unknown, row: any) => row.situacion?.descripcion || '',
        renderCell: (params: any) => (
          <Chip
            label={params.value}
            size="small"
            color={params.row.situacion?.idSituacion === 1 ? 'success' : 'default'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'regimenPensionario',
        headerName: 'Pension',
        width: 130,
        valueGetter: (_value: unknown, row: any) => row.regimenPensionario?.descripcion || '',
      },
      {
        field: 'fechaIngreso',
        headerName: 'F. Ingreso',
        width: 110,
        valueGetter: (_value: unknown, row: any) => row.fechaIngreso ? new Date(row.fechaIngreso).toLocaleDateString('es-PE') : '',
      },
    ] as GridColDef[] : []),
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => handleEdit(params.row.idTrabajador)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row.idTrabajador)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 1,
        mb: 2,
      }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
          Trabajadores
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={!isMobile ? <ImportIcon /> : undefined}
            onClick={() => setImportOpen(true)}
            size={isMobile ? 'small' : 'medium'}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            {isMobile ? 'Importar' : 'Importar Excel'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            size={isMobile ? 'small' : 'medium'}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            {isMobile ? 'Nuevo' : 'Nuevo Trabajador'}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar por nombre, documento o area..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: { xs: '100%', sm: 400 } }}
          fullWidth={isMobile}
        />
      </Paper>

      <Paper sx={{ height: { xs: 'calc(100vh - 220px)', sm: 'calc(100vh - 240px)' } }}>
        <DataGrid
          rows={filteredTrabajadores}
          columns={columns}
          getRowId={(row) => row.idTrabajador}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          disableRowSelectionOnClick
          density={isMobile ? 'compact' : 'standard'}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
            },
          }}
        />
      </Paper>

      <EditTrabajador
        open={dialogOpen}
        trabajador={selectedTrabajador}
        onClose={closeDialog}
      />

      <ImportTrabajadoresDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportComplete={() => fetchTrabajadores()}
      />
    </Box>
  );
};

export default TrabajadoresPage;
